import { useState } from "react";
import { PaymentsSubscriptionStatusResponseDto } from "@advanced-sales-technologies/backend-client/models/PaymentsSubscriptionStatusResponseDto";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowSmallRight } from "react-icons/hi2";

import { Subscription } from "@/types/Subscription.type";
import useSubscription from "@/hooks/useSubscription";
import Button from "@/components/atoms/Button";
import Modal from "@/components/molecules/Modal";
import ChangePaymentIntervalModal from "@/components/molecules/modals/subscription/ChangePaymentIntervalModal";
import KeepCurrentSubscriptionModal from "@/components/molecules/modals/subscription/KeepCurrentSubscriptionModal";
import UpdateSubscriptionModal from "@/components/molecules/modals/subscription/UpdateSubscriptionModal";

type Props = {
  subscription: Subscription;
  paymentType: "month" | "year";
  activeSubscription: Subscription;
  remainingDaysToDowngrade: number;
  plannedDowngrade?: PaymentsSubscriptionStatusResponseDto;
};

const SubscriptionItemButton = ({
  subscription,
  activeSubscription,
  paymentType,
  remainingDaysToDowngrade,
  plannedDowngrade,
}: Props) => {
  const { t } = useTranslation();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isChangeIntervalModalOpen, setIsChangeIntervalModalOpen] = useState(false);
  const [isKeepCurrentSubscriptionModalOpen, setIsKeepCurrentSubscriptionModalOpen] = useState(false);

  const { subscriptionPrice, isUpgrade, isActiveInterval, isActiveSubscription } = useSubscription({
    subscription,
    activeSubscription,
    paymentType,
  });

  const calcDaysLeft = (date: string) => {
    const diff = new Date(date).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const renderButtonContent = () => {
    if (remainingDaysToDowngrade >= 0) {
      return <p className={"w-full text-center font-semibold text-primary"}>{t("settings.plans.yoursNew")}</p>;
    }

    if (isActiveSubscription) {
      if (subscriptionPrice.unit_amount === 0) {
        return <p className={"w-full text-center font-semibold text-primary"}>{t("settings.plans.yoursActive")}</p>;
      }

      return isActiveInterval ? (
        plannedDowngrade?.is_downgrade ? (
          <>
            <Button
              label={t("settings.plans.update.keep.button")}
              variants="secondary"
              size="xs"
              onClick={() => setIsKeepCurrentSubscriptionModalOpen(true)}
              className={"w-full"}
            />
            <p className={"mt-3 text-sm font-medium text-link"}>
              {t("settings.plans.update.downgrade.daysLeft", { count: calcDaysLeft(plannedDowngrade.start_date!) })}
            </p>
          </>
        ) : (
          <p className="w-full text-center font-semibold text-brand-primary">{t("settings.plans.yoursActive")}</p>
        )
      ) : (
        subscriptionPrice.unit_amount !== 0 && (
          <Button
            label={t("settings.plans.paymentInterval", {
              interval: getTranslatedInterval(paymentType),
            })}
            variants="primary"
            size="xs"
            onClick={() => setIsChangeIntervalModalOpen(true)}
            className={"w-full"}
          />
        )
      );
    }

    return (
      <Button
        label={isUpgrade ? t("settings.plans.options.upgrade") : t("settings.plans.options.downgrade")}
        color={isUpgrade ? "brand" : "natural"}
        trailingIcon={<HiOutlineArrowSmallRight className={"size-4 stroke-2"} />}
        variants={isUpgrade ? "secondary" : "primary"}
        size="xs"
        onClick={() => setIsUpdateModalOpen(true)}
        className={"w-full"}
      />
    );
  };

  const getTranslatedInterval = (type: "month" | "year") => {
    if (type === "month") {
      return t("settings.plans.monthly").toLowerCase();
    }
    return t("settings.plans.annually").toLowerCase();
  };

  return (
    <>
      <div>{renderButtonContent()}</div>
      <Modal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        content={UpdateSubscriptionModal}
        contentProps={{
          isUpgrade,
          subscription: subscriptionPrice,
        }}
      />
      <Modal
        isOpen={isChangeIntervalModalOpen}
        onClose={() => setIsChangeIntervalModalOpen(false)}
        content={ChangePaymentIntervalModal}
        contentProps={{
          interval: paymentType,
          subscription: subscriptionPrice,
        }}
      />
      <Modal
        isOpen={isKeepCurrentSubscriptionModalOpen}
        onClose={() => setIsKeepCurrentSubscriptionModalOpen(false)}
        content={KeepCurrentSubscriptionModal}
        contentProps={{
          subscription: subscriptionPrice,
        }}
      />
    </>
  );
};

export default SubscriptionItemButton;
